import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://www.crystalmedia.ca';
const SITE_NAME = 'Crystal Media';
const DEFAULT_IMAGE = `${SITE_URL}/og-default.jpg`;

const DEFAULT_DESCRIPTION = 'Crystal Media is a luxury wedding photographer and wedding videographer creating cinematic wedding films and timeless photography for Pakistani, Indian, Punjabi, Sikh, Muslim, Hindu, and multicultural weddings in Toronto, Vancouver, Montreal, Houston, Miami, Texas, and across Canada and the USA.';

const CORE_KEYWORDS = [
  'Wedding Photographer', 'Wedding Videographer', 'Wedding Films Toronto', 'Wedding Films USA',
  'Luxury Wedding Photographer', 'Luxury Wedding Videographer', 'Cinematic Wedding Films',
  'Wedding Photography Toronto', 'Wedding Videography Toronto',
  'Destination Wedding Videographer', 'Destination Wedding Photographer',
  'Pakistani Wedding Highlights', 'Pakistani Wedding Photographer', 'Pakistani Wedding Videographer',
  'Indian Wedding Photographer', 'Indian Wedding Videographer',
  'Punjabi Wedding Photographer', 'Punjabi Wedding Videographer',
  'Sikh Wedding Photographer', 'Sikh Wedding Videographer',
  'Muslim Wedding Photographer', 'Muslim Wedding Videographer',
  'Hindu Wedding Photographer', 'Hindu Wedding Videographer',
  'South Asian Wedding Photographer', 'South Asian Wedding Videographer',
  'Multicultural Wedding Photographer', 'Fusion Wedding Videographer',
  'Wedding Photographer Vancouver', 'Wedding Videographer Vancouver',
  'Wedding Photographer Montreal', 'Wedding Photographer Houston',
  'Wedding Photographer Miami', 'Wedding Photographer Texas',
];

const SEOHead = ({
  title,
  description,
  keywords = [],
  image,
  url,
  type = 'website',
  jsonLd,
  noindex = false,
  siteSettings = {},
}) => {
  const ogTitle = siteSettings.og_title || '';
  const ogDesc = siteSettings.og_description || '';
  const ogImg = siteSettings.og_image_url || '';
  const faviconUrl = siteSettings.favicon_url || '';

  const fullTitle = title ? `${title} | ${SITE_NAME}` : (ogTitle || `${SITE_NAME} — Luxury Wedding Photography & Cinematic Wedding Films | Toronto, Canada & USA`);
  const metaDescription = description || ogDesc || DEFAULT_DESCRIPTION;
  const metaImage = image || ogImg || DEFAULT_IMAGE;
  const canonicalUrl = url ? `${SITE_URL}${url}` : undefined;
  const allKeywords = [...new Set([...keywords, ...CORE_KEYWORDS])];

  // Keep JSON-LD brand assets in sync with admin settings (logo + share image)
  const brandLogo = siteSettings.photo_logo_url || siteSettings.logo_url || '';
  const brandImage = ogImg || DEFAULT_IMAGE;
  const syncBrand = (node) => {
    if (Array.isArray(node)) return node.map(syncBrand);
    if (node && typeof node === 'object') {
      const out = {};
      for (const [k, v] of Object.entries(node)) {
        if (k === 'logo' && brandLogo) out[k] = typeof v === 'string' ? brandLogo : { ...v, url: brandLogo };
        else if (k === 'logo' && typeof v === 'string' && v.endsWith('/logo.png')) out[k] = brandImage;
        else if (k === 'logo' && v && typeof v === 'object' && String(v.url || '').endsWith('/logo.png')) out[k] = { ...v, url: brandImage };
        else if (k === 'image' && v === DEFAULT_IMAGE) out[k] = brandImage;
        else out[k] = syncBrand(v);
      }
      return out;
    }
    return node;
  };
  const structuredData = jsonLd ? syncBrand(Array.isArray(jsonLd) ? jsonLd.filter(Boolean) : jsonLd) : null;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={allKeywords.join(', ')} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* Favicon */}
      {faviconUrl && <link rel="icon" href={faviconUrl} />}
      {faviconUrl && <link rel="apple-touch-icon" href={faviconUrl} />}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={metaImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={fullTitle} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_CA" />
      <meta property="og:locale:alternate" content="en_US" />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={metaImage} />
      <meta name="twitter:image:alt" content={fullTitle} />

      {/* Canonical */}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      {!noindex && <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />}

      {/* Geo */}
      <meta name="geo.region" content="CA-ON" />
      <meta name="geo.placename" content="Toronto" />

      {/* JSON-LD Structured Data */}
      {structuredData && (
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      )}
    </Helmet>
  );
};

/* ─── Pre-built JSON-LD Schemas ─── */

export const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${SITE_URL}/#website`,
  name: SITE_NAME,
  url: SITE_URL,
  inLanguage: 'en-CA',
  publisher: { '@id': `${SITE_URL}/#business` },
  potentialAction: {
    '@type': 'SearchAction',
    target: { '@type': 'EntryPoint', urlTemplate: `${SITE_URL}/collections?tag={search_term_string}` },
    'query-input': 'required name=search_term_string',
  },
};

export const faqSchema = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.filter(f => f?.question && f?.answer).map(f => ({
    '@type': 'Question',
    name: f.question,
    acceptedAnswer: { '@type': 'Answer', text: f.answer },
  })),
});

export const imageGallerySchema = (collection, images = []) => ({
  '@context': 'https://schema.org',
  '@type': 'ImageGallery',
  name: `${collection.couple} — ${collection.location} Wedding Photography`,
  description: `Luxury wedding photography for ${collection.couple} in ${collection.location} by Crystal Media.`,
  url: `${SITE_URL}/collection/${collection.id}`,
  author: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
  primaryImageOfPage: collection.cover_image_url ? { '@type': 'ImageObject', contentUrl: collection.cover_image_url } : undefined,
  image: images.slice(0, 12).map(u => ({
    '@type': 'ImageObject',
    contentUrl: u,
    creditText: SITE_NAME,
    copyrightNotice: `© ${SITE_NAME}`,
    creator: { '@type': 'Organization', name: SITE_NAME },
  })),
});

export const seoServiceSchema = (page) => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: page.h1,
  serviceType: page.service === 'videographer' ? 'Wedding Videography' : 'Wedding Photography',
  description: page.meta_description,
  url: `${SITE_URL}/${page.slug}`,
  provider: { '@id': `${SITE_URL}/#business`, '@type': 'ProfessionalService', name: SITE_NAME, url: SITE_URL, telephone: '+1-416-510-2424' },
  areaServed: page.city ? { '@type': 'City', name: page.city } : [{ '@type': 'Country', name: 'Canada' }, { '@type': 'Country', name: 'United States' }],
  audience: page.culture ? { '@type': 'Audience', audienceType: `${page.culture} weddings` } : undefined,
});

export const videoListSchema = (films) => ({
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'Crystal Media Wedding Films',
  itemListElement: films.slice(0, 20).map((f, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    item: {
      '@type': 'VideoObject',
      name: `${f.title} — Wedding Film`,
      description: f.subtitle || `Cinematic wedding film for ${f.title} by Crystal Media, luxury wedding videographer.`,
      thumbnailUrl: f.thumbnail_url || (f.youtube_id ? `https://i.ytimg.com/vi/${f.youtube_id}/hqdefault.jpg` : DEFAULT_IMAGE),
      uploadDate: f.created_at || '2024-01-01',
      contentUrl: f.video_url_high || f.video_url || f.video_url_low || undefined,
      embedUrl: f.youtube_id ? `https://www.youtube.com/embed/${f.youtube_id}` : undefined,
      publisher: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
    },
  })),
});

export const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Crystal Media',
  url: SITE_URL,
  logo: `${SITE_URL}/logo.png`,
  description: 'Crystal Media creates luxury wedding films and photography for South Asian and multicultural weddings across Toronto, Vancouver, Montreal, Miami, Houston, Texas, and destinations throughout Canada and the USA.',
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+1-416-510-2424',
    contactType: 'customer service',
    areaServed: ['CA', 'US'],
    availableLanguage: ['English', 'Urdu', 'Hindi', 'Punjabi'],
  },
  sameAs: [
    'https://www.instagram.com/crystalmedia.ca',
    'https://www.instagram.com/creativedirectorkhan',
    'https://creativedirectorkhan.com',
  ],
  foundingDate: '2012',
  numberOfEmployees: { '@type': 'QuantitativeValue', minValue: 5, maxValue: 15 },
};

export const localBusinessSchema = {
  '@context': 'https://schema.org',
  '@type': 'ProfessionalService',
  '@id': `${SITE_URL}/#business`,
  name: 'Crystal Media',
  description: 'Premier wedding photographer and wedding videographer offering luxury wedding photography and cinematic wedding films in Toronto, Vancouver, Montreal, Miami, Houston, Texas, and throughout Canada and the USA. Specializing in Pakistani, Indian, Punjabi, Sikh, Muslim, Hindu, and multicultural weddings.',
  url: SITE_URL,
  telephone: '+1-416-510-2424',
  email: 'contact@crystalmedia.ca',
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Toronto',
    addressRegion: 'ON',
    addressCountry: 'CA',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: 43.6532,
    longitude: -79.3832,
  },
  priceRange: '$$$$',
  image: DEFAULT_IMAGE,
  areaServed: [
    { '@type': 'City', name: 'Toronto' },
    { '@type': 'City', name: 'Vancouver' },
    { '@type': 'City', name: 'Montreal' },
    { '@type': 'City', name: 'Miami' },
    { '@type': 'City', name: 'Houston' },
    { '@type': 'State', name: 'Texas' },
    { '@type': 'Country', name: 'Canada' },
    { '@type': 'Country', name: 'United States' },
  ],
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Wedding Services',
    itemListElement: [
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Luxury Wedding Photography' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Cinematic Wedding Films' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Pakistani Wedding Highlights' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'South Asian Wedding Photography' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Destination Wedding Videography' } },
    ],
  },
  sameAs: [
    'https://www.instagram.com/crystalmedia.ca',
    'https://www.instagram.com/creativedirectorkhan',
  ],
};

export const blogPostSchema = (post) => ({
  '@context': 'https://schema.org',
  '@type': 'BlogPosting',
  headline: post.title,
  description: post.excerpt || post.seo_description || '',
  image: post.featured_image_url || DEFAULT_IMAGE,
  author: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
  publisher: {
    '@type': 'Organization',
    name: SITE_NAME,
    logo: { '@type': 'ImageObject', url: `${SITE_URL}/logo.png` },
  },
  datePublished: post.published_at || post.created_at,
  dateModified: post.updated_at,
  url: `${SITE_URL}/blog/${post.slug}`,
  mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE_URL}/blog/${post.slug}` },
});

export const videoSchema = (video) => ({
  '@context': 'https://schema.org',
  '@type': 'VideoObject',
  name: video.title || 'Crystal Media Wedding Film',
  description: video.description || 'Cinematic wedding film by Crystal Media, luxury wedding videographer in Toronto, Canada.',
  thumbnailUrl: video.thumbnail || DEFAULT_IMAGE,
  uploadDate: video.date || new Date().toISOString(),
  contentUrl: video.url,
  publisher: { '@type': 'Organization', name: SITE_NAME },
});

export const reviewSchema = (testimonials) => ({
  '@context': 'https://schema.org',
  '@type': 'ProfessionalService',
  '@id': `${SITE_URL}/#business`,
  name: 'Crystal Media',
  url: SITE_URL,
  image: DEFAULT_IMAGE,
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '5',
    bestRating: '5',
    reviewCount: String(Math.max(testimonials.length, 1)),
  },
  review: testimonials.slice(0, 10).map(t => ({
    '@type': 'Review',
    author: { '@type': 'Person', name: t.name },
    reviewBody: t.text,
    datePublished: t.created_at ? String(t.created_at).slice(0, 10) : undefined,
    reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' },
  })),
});

export const breadcrumbSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: item.name,
    item: `${SITE_URL}${item.path}`,
  })),
});

export default SEOHead;
